import {
  faCircleDollar,
  faCoins,
  faEllipsisVertical,
  faKey
} from '@fortawesome/pro-duotone-svg-icons'
import {
  Box,
  Card,
  IconButton,
  ListItemText,
  MenuItem,
  MenuList
} from '@mui/material'
import { usePopover } from 'minimal-shared/hooks'
import { memo } from 'react'

import foundersKeyImage from 'assets/qbx-founders-key-lit.jpg'
import { CustomPopover } from 'src/components/shared/custom-popover'
import { Image } from 'src/components/shared/image'
import { Label } from 'src/components/shared/label'
import FaIcon from 'src/components/shared/faIcon/FaIcon'
import {
  GET_KEYS_ON_BLUR_URL,
  GET_KEYS_ON_OPEN_SEA_URL,
  OPEN_SEA_BASE_URL,
  QBX_LEVEL_COUNT_VALUE
} from 'src/constants/staking'
import { openExternalUrl } from 'src/util/app/app.helpers'
import { formatCurrencyShort } from 'src/util/number.helpers'
import { formatTicks } from 'src/util/string.helpers'
import FoundersKeyLoading from './FoundersKeyLoading'

const FoundersKeys = ({
  t,
  connectedWallet,
  stakingProfile: { priceOfQBX },
  walletProfile: { nfts, isProcessing }
}) => {
  const amountOfKeys = nfts.length
  const countedQBX = amountOfKeys * QBX_LEVEL_COUNT_VALUE

  const menuActions = usePopover()

  const renderMenuActions = () => {
    const menuItems = [
      {
        label: 'view-on-opensea',
        url: `${OPEN_SEA_BASE_URL}${connectedWallet}`
      },
      {
        label: 'get-keys-on-opensea',
        url: GET_KEYS_ON_OPEN_SEA_URL
      },
      {
        label: 'get-keys-on-blur',
        url: GET_KEYS_ON_BLUR_URL
      }
    ]
    return (
      <CustomPopover
        open={menuActions.open}
        anchorEl={menuActions.anchorEl}
        onClose={menuActions.onClose}
        slotProps={{ arrow: { placement: 'right-top' } }}
      >
        <MenuList>
          {menuItems.map(({ label, url }) => (
            <MenuItem key={label} onClick={() => openExternalUrl(url)}>
              {t(`brand:staking.${label}`)}
            </MenuItem>
          ))}
        </MenuList>
      </CustomPopover>
    )
  }

  const labels = [
    {
      icon: faKey,
      label: `${t('brand:staking.amount-of-keys')}: ${amountOfKeys}`
    },
    {
      icon: faCoins,
      label: `${t('brand:staking.amount-of-level')}: ${formatTicks(countedQBX)}`
    },
    {
      icon: faCircleDollar,
      label: `${t('brand:staking.dollar-value-of-level')}: $${formatCurrencyShort(countedQBX * priceOfQBX)}`
    }
  ]

  return (
    <>
      <Card>
        <Box sx={{ p: 1, gap: 0.5, display: 'flex' }}>
          <Image
            src={foundersKeyImage}
            sx={{ width: 1, height: 164, borderRadius: 1 }}
          />
        </Box>
        <ListItemText
          sx={[(theme) => ({ p: theme.spacing(2.5, 2.5, 2, 2.5) })]}
          primary={
            <Label color='default'>{t('brand:staking.coming-soon')}</Label>
          }
          secondary={t('brand:staking.founders-keys')}
          slotProps={{
            primary: {
              sx: { typography: 'caption', color: 'text.disabled' }
            },
            secondary: {
              noWrap: true,
              component: 'h6',
              color: 'inherit',
              sx: { mt: 1, color: 'text.primary', typography: 'subtitle1' }
            }
          }}
        />
        <Box
          sx={[
            (theme) => ({
              gap: 1.5,
              display: 'flex',
              position: 'relative',
              flexDirection: 'column',
              p: theme.spacing(0, 2.5, 2.5, 2.5)
            })
          ]}
        >
          <IconButton
            onClick={menuActions.onOpen}
            sx={{
              position: 'absolute',
              bottom: 20,
              right: 8,
              height: 36,
              width: 36
            }}
          >
            <FaIcon icon={faEllipsisVertical} style={{ fontSize: 20 }} />
          </IconButton>
          {isProcessing ? (
            <FoundersKeyLoading />
          ) : (
            labels.map(({ label, icon }) => (
              <Box
                key={label}
                sx={[
                  {
                    gap: 0.8,
                    display: 'flex',
                    typography: 'body2',
                    alignItems: 'center'
                  }
                ]}
              >
                <FaIcon
                  icon={icon}
                  style={{
                    color: 'var(--palette-primary-main)',
                    fontSize: 20,
                    verticalAlign: 'middle'
                  }}
                />
                {label}
              </Box>
            ))
          )}
        </Box>
      </Card>
      {renderMenuActions()}
    </>
  )
}

export default memo(FoundersKeys)
